import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FormInput,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '@/components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { Card } from '@/components/ui/card';
import { Button } from '@design-system';
import { useClientSearch } from '../hooks/use-client-search';
import { getErrorMessage } from '../utils/get-error-message';

const schema = z.object({
  clientId: z.string().min(1, 'Client ID is required'),
});

export function ClientViewerForm() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialClientIdRef = useRef(searchParams.get('clientId') ?? '');
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { clientId: initialClientIdRef.current },
  });

  const {
    searchClient,
    client,
    advisor,
    surveys,
    isPending: isSearching,
    isError: isSearchError,
    error: searchError,
  } = useClientSearch();

  useEffect(() => {
    if (initialClientIdRef.current) {
      searchClient(initialClientIdRef.current);
    }
  }, [searchClient]);

  const onSearchClient = (data: z.infer<typeof schema>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('clientId', data.clientId);
        return next;
      },
      { replace: true },
    );
    setCopied(false);
    searchClient(data.clientId);
  };

  const onCopyJson = async () => {
    if (!client) return;
    await navigator.clipboard.writeText(
      JSON.stringify({ client, surveys: surveys ?? [] }, null, 2),
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit(onSearchClient)}
        className="space-y-4"
      >
        <FormInput
          label="Client ID"
          id="clientId"
          type="text"
          placeholder="Enter client ID"
          error={errors.clientId}
          register={register('clientId')}
          required={true}
        />

        <PrimarySecondaryButtonContainer>
          <SubmitButton
            label="Search Client"
            isLoading={isSearching}
          />
        </PrimarySecondaryButtonContainer>

        {isSearchError && (
          <FormErrorAlert
            mainErrorMessage={getErrorMessage(searchError, 'Client not found')}
            className="mt-4"
          />
        )}
      </form>

      {client && (
        <Card className="space-y-4 p-6">
          {advisor && (
            <div className="border-b pb-3 text-sm text-gray-700">
              <strong>Owning advisor:</strong> {advisor.advisorFirstName}{' '}
              {advisor.advisorLastName} ({advisor.advisorId}) —{' '}
              {advisor.advisorEmail}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-medium">Client Record</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCopyJson}
              >
                {copied ? 'Copied' : 'Copy JSON'}
              </Button>
            </div>
            <pre className="max-h-[500px] overflow-auto rounded bg-gray-50 p-3 text-xs font-mono">
              {JSON.stringify(client, null, 2)}
            </pre>
          </div>

          <div className="space-y-2">
            <h4 className="text-base font-medium">
              Survey Answers{surveys ? ` (${surveys.length})` : ''}
            </h4>
            <pre className="max-h-[500px] overflow-auto rounded bg-gray-50 p-3 text-xs font-mono">
              {JSON.stringify(surveys ?? [], null, 2)}
            </pre>
          </div>
        </Card>
      )}
    </div>
  );
}
